<script lang="ts">
const dialKeys = '123456789*0#'
</script>
<script setup lang="ts">
import AuthorizeView from '@/components/common/AuthorizeView.vue';
import { Notifications } from '@/composables';
import { useDialerStore } from '@/store/dialer.store';
import { useIdentityStore } from '@/store/identity.store';
import {
    ExclamationCircleIcon,
    PhoneIcon,
    PhoneIncomingIcon,
    PhoneMissedCallIcon,
    PhoneOutgoingIcon,
} from '@heroicons/vue/solid';
import { Mask } from 'maska';
import { storeToRefs } from 'pinia';
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n({});
const dialerStore = useDialerStore();
const identityStore = useIdentityStore();
const {
    errors,
    seconds,
    callTimer,
    incomingCall,
    isActiveCall,
    currentCall,
    isIncomingCall,
    isTwilioDeviceReady,
    isTwilioDeviceConnected,
    hasIncomingConnection,
    hasOutgoingConnection,
    ready,
} = storeToRefs(dialerStore);
const { onReadyClicked, acceptCallWrapper, endCallWrapper, rejectCallWrapper } = dialerStore;
const myInput = ref<any>();
const pressedKey = ref<string | null>('');
const showDialer = ref(true);
const maskedPhone = ref<string>('');
const phoneNumber = ref<string>('');
const isStartingCall = ref(false);

const messagingPermission = computed(() =>
    identityStore.authorized(
        ['Permissions.Messaging.Messages.Search'] as string[] | undefined,
        undefined
    )
);
const dialPermission = computed(() =>
    identityStore.authorized(
        ['Permissions.Messaging.Messages.Dial'] as string[] | undefined,
        undefined
    )
);
const pressKey = (key) => {
    maskedPhone.value += key;
    phoneNumber.value += key;
    focusInput();
};
const removeKey = () => {
    if (phoneNumber.value) {
        maskedPhone.value = maskedPhone.value.slice(0, -1);
        phoneNumber.value = phoneNumber.value.slice(0, -1);
    }
    focusInput();
};
const toggleShowDialer = () => {
    showDialer.value = !showDialer.value;
    focusInput();
};
// Verify microphone access before starting an outgoing Twilio call so the user
// gets a clear message instead of a silent/failed call when no mic is available,
// permission is denied, or the browser lacks the media APIs.
const ensureMicrophoneAccess = async (): Promise<boolean> => {
    if (!navigator.mediaDevices?.getUserMedia) {
        Notifications.Error(
            t(
                'Microphone access is not supported by this browser. Please use a supported browser to place a call.'
            )
        );
        return false;
    }
    try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        // Release the preflight stream immediately so it does not hold the device.
        stream.getTracks().forEach((track) => track.stop());
        return true;
    } catch (error) {
        console.error('Microphone preflight failed before starting outgoing call:', error);
        Notifications.Error(
            t(
                'Microphone access is required to place a call. Please connect a microphone and allow access in your browser, then try again.'
            )
        );
        return false;
    }
};
const startCallWrapper = async () => {
    // Guard against rapid repeated clicks while the async microphone preflight is pending,
    // which could otherwise trigger multiple preflights or multiple outbound call starts.
    if (isStartingCall.value) return;
    isStartingCall.value = true;
    try {
        if (!(await ensureMicrophoneAccess())) return;
        dialerStore.startCallWrapper(phoneNumber.value);
    } finally {
        isStartingCall.value = false;
    }
};
onMounted(() => {
    focusInput();
});
onUnmounted(() => {
    dialerStore.destroy();
});
const onMaska = (event) => {
    phoneNumber.value = event?.detail?.unmasked;
};
const maskPhone = (value: string | undefined) => {
    if (value) {
        const m = new Mask({
            mask: ['NNN', '(NNN) NNN-NNNN', 'NNN-NNN-NNNN-A'],
            tokens: {
                N: { pattern: /[0-9!#]/ },
                A: { pattern: /[0-9!#!+]/, multiple: true },
            },
            eager: true,
        });
        return m.masked(value);
    }
    return '';
};
function focusInput() {
    nextTick(() => {
        myInput.value?.focus();
    });
}
watch(
    () => [
        dialerStore.isActiveCall,
        dialerStore.isTwilioDeviceReady,
        dialerStore.isTwilioDeviceConnected,
        dialerStore.hasIncomingConnection,
    ],
    ([active, ready, connected, incoming]) => {
        if (incoming) {
            showDialer.value = true;
        }
        if (!active && ready && !connected && !incoming) {
            focusInput();
        }
    }
);
watch(
    () => phoneNumber.value,
    (newVal, oldVal) => {
        if (newVal.length > oldVal.length) {
            const lastChar = newVal.slice(-1);
            pressedKey.value = lastChar ?? '';
            setTimeout(() => {
                if (pressedKey.value === lastChar) {
                    pressedKey.value = null;
                }
            }, 150);
        }
    }
);
</script>
<template>
    <div v-if="messagingPermission">
        <div>
            <transition name="slide-up-fade" mode="out-in" @after-enter="focusInput">
                <button
                    v-if="!ready"
                    id="ready-button"
                    :class="[
                        'fixed right-7 bottom-1 shadow-lg bg-orange-500 hover:bg-orange-700 text-white font-bold py-2 px-4 rounded',
                        hasIncomingConnection ? 'dialer--incoming-call' : '',
                    ]"
                    style="z-index: 10"
                    @click="onReadyClicked"
                >
                    I'm ready
                </button>
                <div
                    v-else
                    :class="[
                        'dialer-container',
                        hasIncomingConnection ? 'dialer--incoming-call' : '',
                    ]"
                >
                    <transition name="slide-up-fade" mode="out-in" @after-enter="focusInput">
                        <button
                            v-if="!showDialer"
                            key="button"
                            :class="[
                                'show-button',
                                'text-white font-bold  rounded bg-gray-600/30',
                                'inline-flex items-center',
                            ]"
                            @click="toggleShowDialer"
                        >
                            <transition name="fade-slide" mode="out-in">
                                <span v-if="seconds > 0 && !showDialer" class="px-3">{{
                                    callTimer
                                }}</span>
                            </transition>
                            <span
                                :class="[
                                    showDialer
                                        ? 'bg-blue-500 hover:bg-blue-700'
                                        : 'bg-green-500 hover:bg-green-700',
                                    'p-1 rounded transition-colors duration-300',
                                ]"
                            >
                                <PhoneIcon class="h-5 w-5" />
                            </span>
                        </button>

                        <div v-else key="dialer">
                            <transition
                                name="slide-side-fade"
                                mode="out-in"
                                @after-enter="focusInput"
                            >
                                <div
                                    v-if="
                                        hasIncomingConnection ||
                                        hasOutgoingConnection ||
                                        isTwilioDeviceConnected
                                    "
                                    :class="[
                                        'dialer-choice-container',
                                        showDialer ? 'dialer--show-top' : 'dialer--simple--hide',
                                    ]"
                                >
                                    <button
                                        v-if="showDialer"
                                        :class="[
                                            'hide-button',
                                            ' rounded-t-lg',
                                            'text-white font-bold  rounded bg-gray-600/30',
                                            'inline-flex items-center',
                                        ]"
                                        @click="toggleShowDialer"
                                    >
                                        <span
                                            :class="[
                                                'bg-blue-500 hover:bg-blue-700',
                                                'p-1 rounded transition-colors duration-300',
                                            ]"
                                        >
                                            <PhoneIcon class="h-5 w-5" />
                                        </span>
                                    </button>
                                    <transition-group
                                        name="fade"
                                        tag="div"
                                        class="relative"
                                        @after-enter="focusInput"
                                    >
                                        <div
                                            v-if="hasIncomingConnection"
                                            key="one"
                                            class="call-card bg-gray-600/10 backdrop-blur-sm border border-white/20 shadow-lg rounded p-2"
                                            :class="{
                                                'mb-2':
                                                    hasIncomingConnection ||
                                                    hasOutgoingConnection ||
                                                    isTwilioDeviceConnected,
                                            }"
                                        >
                                            <div class="block text-sm">
                                                <p
                                                    class="text-left text-gray-400 font-bold text-lg truncate"
                                                >
                                                    {{ maskPhone(incomingCall?.origin) }}
                                                </p>
                                                <div
                                                    class="flex text-center px-2 space-x-2 text-gray-400"
                                                >
                                                    <PhoneIncomingIcon
                                                        class="h-5 w-5 text-emerald-400"
                                                    />
                                                    <span>{{ t('incoming call ...') }}</span>
                                                </div>
                                            </div>
                                            <div class="p-2 grid grid-cols-3 gap-2">
                                                <button
                                                    type="button"
                                                    class="shadow-md col-start-1 bg-red-500 hover:bg-red-700 flex items-center justify-center font-bold p-4 rounded-full"
                                                    @click="rejectCallWrapper"
                                                >
                                                    <PhoneMissedCallIcon
                                                        class="h-7 w-7 flex-shrink-0 text-white"
                                                        aria-hidden="true"
                                                    />
                                                </button>
                                                <button
                                                    type="button"
                                                    class="shadow-md col-start-3 bg-green-500 hover:bg-green-700 flex justify-center items-center font-bold p-4 rounded-full"
                                                    @click="acceptCallWrapper"
                                                >
                                                    <PhoneIcon
                                                        class="h-7 w-7 flex-shrink-0 text-white"
                                                        aria-hidden="true"
                                                    />
                                                </button>
                                            </div>
                                        </div>
                                        <div
                                            v-if="hasOutgoingConnection || isTwilioDeviceConnected"
                                            key="two"
                                            class="call-card bg-gray-600/10 backdrop-blur-sm border border-white/20 shadow-lg rounded p-2"
                                        >
                                            <div class="block text-sm">
                                                <template v-if="isIncomingCall">
                                                    <p
                                                        class="text-left text-gray-400 font-bold text-lg truncate"
                                                    >
                                                        {{ maskPhone(currentCall?.origin) }}
                                                    </p>
                                                    <div
                                                        class="flex text-center px-2 space-x-2 text-gray-400"
                                                    >
                                                        <PhoneIncomingIcon
                                                            class="h-5 w-5 text-emerald-400"
                                                        />
                                                        <span>{{
                                                            isTwilioDeviceConnected
                                                                ? callTimer
                                                                : t('incoming call ...')
                                                        }}</span>
                                                    </div>
                                                </template>
                                                <template v-else>
                                                    <p
                                                        class="text-left text-gray-400 font-bold text-lg truncate"
                                                    >
                                                        {{ maskPhone(currentCall?.origin) }}
                                                    </p>
                                                    <div
                                                        class="flex text-center px-2 space-x-2 text-gray-400"
                                                    >
                                                        <PhoneOutgoingIcon
                                                            class="h-5 w-5 text-emerald-400"
                                                        />
                                                        <span>{{
                                                            isTwilioDeviceConnected
                                                                ? callTimer
                                                                : t('calling ...')
                                                        }}</span>
                                                    </div>
                                                </template>
                                            </div>
                                            <div class="grid grid-cols-3 gap-2">
                                                <button
                                                    :disabled="
                                                        !isTwilioDeviceReady ||
                                                        !isTwilioDeviceConnected
                                                    "
                                                    type="button"
                                                    class="shadow-md col-start-2 bg-red-500 hover:bg-red-700 flex items-center justify-center font-bold p-4 rounded-full"
                                                    @click="endCallWrapper"
                                                >
                                                    <PhoneMissedCallIcon
                                                        class="h-7 w-7 flex-shrink-0 text-white"
                                                        aria-hidden="true"
                                                    />
                                                </button>
                                            </div>
                                        </div>
                                    </transition-group>
                                </div>
                                <div
                                    v-else-if="!isActiveCall && !isTwilioDeviceConnected"
                                    key="three"
                                    :class="[
                                        'dialer-panel',
                                        'dialer',
                                        showDialer ? 'dialer--show-top' : 'dialer--hide',
                                    ]"
                                >
                                    <button
                                        v-if="showDialer"
                                        :class="[
                                            'hide-button',
                                            ' rounded-t-lg',
                                            'text-white font-bold  rounded bg-gray-600/30',
                                            'inline-flex items-center',
                                        ]"
                                        @click="toggleShowDialer"
                                    >
                                        <span
                                            :class="[
                                                'bg-blue-500 hover:bg-blue-700',
                                                'p-1 rounded transition-colors duration-300',
                                            ]"
                                        >
                                            <PhoneIcon class="h-5 w-5" />
                                        </span>
                                    </button>
                                    <div class="text-center text-gray-400">
                                        <div v-if="errors" class="flex flex-col">
                                            <div class="flex justify-center items-center space-x-2">
                                                <ExclamationCircleIcon
                                                    class="h-5 w-5 text-yellow-400"
                                                    aria-hidden="true"
                                                />
                                                <h3 class="text-xs font-medium text-yellow-800">
                                                    {{ t('Attention') }}
                                                </h3>
                                            </div>
                                            <div class="ml-1">
                                                <p class="text-xs text-yellow-700">
                                                    {{ errors }}
                                                </p>
                                            </div>
                                        </div>
                                        <div v-else>{{ t('Standing by') }}</div>
                                    </div>
                                    <div class="flex justify-center mb-4">
                                        <input
                                            ref="myInput"
                                            v-model="maskedPhone"
                                            v-maska
                                            data-maska="['NNN','(NNN) NNN-NNNN','NNN-NNN-NNNN-A']"
                                            data-maska-tokens="N:[0-9!#]|A:[0-9!#!+]:multiple"
                                            :disabled="
                                                !dialPermission ||
                                                !isTwilioDeviceReady ||
                                                isTwilioDeviceConnected
                                            "
                                            :placeholder="t('Phone Number')"
                                            class="text-lg font-bold p-2 text-center text-gray-400 border-none outline-none focus:ring-0 focus:outline-none rounded max-w"
                                            @maska="onMaska"
                                        />
                                    </div>
                                    <AuthorizeView
                                        :all-of="['Permissions.Messaging.Messages.Dial']"
                                    >
                                        <div class="dial-pad grid grid-cols-3 gap-2 mb-4">
                                            <button
                                                v-for="dialKey in dialKeys"
                                                :key="dialKey"
                                                :disabled="!isTwilioDeviceReady"
                                                :class="[
                                                    'bg-gray-300 shadow-md hover:bg-gray-400 text-gray-800 font-bold p-4 rounded-full',
                                                    pressedKey === dialKey
                                                        ? 'bg-gray-400'
                                                        : 'bg-gray-300',
                                                ]"
                                                type="button"
                                                @click="pressKey(dialKey)"
                                            >
                                                {{ dialKey }}
                                            </button>
                                        </div>
                                    </AuthorizeView>
                                    <div class="grid grid-cols-3 gap-2">
                                        <button
                                            :disabled="
                                                !isTwilioDeviceReady ||
                                                isTwilioDeviceConnected ||
                                                phoneNumber.length === 0 ||
                                                isStartingCall
                                            "
                                            type="button"
                                            class="shadow-md col-start-2 bg-green-500 hover:bg-green-700 flex justify-center items-center font-bold p-4 rounded-full"
                                            @click="startCallWrapper"
                                        >
                                            <PhoneIcon
                                                class="h-7 w-7 flex-shrink-0 text-white"
                                                aria-hidden="true"
                                            />
                                        </button>
                                        <button
                                            :disabled="
                                                !isTwilioDeviceReady ||
                                                isTwilioDeviceConnected ||
                                                hasIncomingConnection
                                            "
                                            class="flex justify-center items-center py-2 px-4 border-gray-300 font-bold rounded"
                                            @click="removeKey"
                                        >
                                            <svg
                                                class="h-8 w-8 text-gray-300 hover:text-gray-400"
                                                xmlns="http://www.w3.org/2000/svg"
                                                viewBox="0 0 24 24"
                                                fill="currentColor"
                                            >
                                                <path
                                                    fill-rule="evenodd"
                                                    d="M2.515 10.674a1.875 1.875 0 0 0 0 2.652L8.89 19.7c.352.351.829.549 1.326.549H19.5a3 3 0 0 0 3-3V6.75a3 3 0 0 0-3-3h-9.284c-.497 0-.974.198-1.326.55l-6.375 6.374ZM12.53 9.22a.75.75 0 1 0-1.06 1.06L13.19 12l-1.72 1.72a.75.75 0 1 0 1.06 1.06l1.72-1.72 1.72 1.72a.75.75 0 1 0 1.06-1.06L15.31 12l1.72-1.72a.75.75 0 1 0-1.06-1.06l-1.72 1.72-1.72-1.72Z"
                                                    clip-rule="evenodd"
                                                />
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            </transition>
                        </div>
                    </transition>
                </div>
            </transition>
        </div>
    </div>
</template>
<style scoped>
.dialer-container {
    position: fixed;
    right: 0px;
    /* 28px */
    bottom: 0;
    transform-origin: right bottom;
    /* Scale from bottom-right */
    z-index: 10;
}

.dialer {
    @apply bg-gray-600/10 backdrop-blur-sm border border-white/20 fixed right-7 p-5 shadow-lg rounded;
    /* right: 30px, padding: 20px 30px */
    width: 295px;
    bottom: 0;
    border-top-left-radius: 5px;
    transition: all 1s;
    transform: translate3d(0, 0, 0);
    backface-visibility: hidden;
    perspective: 1000px;
}

.dialer-choice-container {
    @apply fixed right-7;
    /* right: 30px, padding: 20px 30px */
    width: 295px;
    bottom: 0;
    border-top-left-radius: 5px;
    transition: all 1s;
    transform: translate3d(0, 0, 0);
    backface-visibility: hidden;
    perspective: 1000px;
}

.dialer--hide {
    bottom: -452px;
}

.dialer--simple--hide {
    bottom: -192px;
}

.dialer--show {
    bottom: 0;
}

@keyframes shake {
    5%,
    45% {
        transform: translate3d(-1px, 0, 0);
    }

    10%,
    40% {
        transform: translate3d(1px, 0, 0);
    }

    15%,
    25%,
    35% {
        transform: translate3d(-2px, 0, 0);
    }

    20%,
    30% {
        transform: translate3d(2px, 0, 0);
    }
}

.dialer--incoming-call {
    animation: shake 1s cubic-bezier(0.36, 0.07, 0.19, 0.97) infinite both;
}

.hide-button {
    @apply absolute right-0 rounded-t-lg;
    top: -28px;
}

.show-button {
    @apply absolute rounded-t-lg;
    bottom: 0;
    right: 29px;
}

/* Fade transition*/
.call-card {
    transition:
        opacity 0.3s ease,
        transform 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
    transform: translateX(10px);
}

.fade-enter-to,
.fade-leave-from {
    opacity: 1;
    transform: translateX(0);
}

.fade-leave-active {
    position: absolute;
    width: 100%;
    z-index: 0;
}

.fade-move {
    transition: transform 0.3s ease;
}

/* Fade-slide transition */
.fade-slide-enter-active,
.fade-slide-leave-active {
    transition:
        opacity 0.3s ease,
        transform 0.3s ease;
}

.fade-slide-enter-from,
.fade-slide-leave-to {
    opacity: 0;
    transform: translateX(10px);
}

.fade-slide-enter-to,
.fade-slide-leave-from {
    opacity: 1;
    transform: translateX(0);
}

/* Slide-up-fade transition */
.slide-up-fade-enter-active {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-up-fade-leave-active {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-up-fade-enter-from {
    opacity: 0;
    transform: translateY(20px);
}

.slide-up-fade-leave-to {
    opacity: 0;
    transform: translateY(-20px);
}

/* Slide-side transition */

.slide-side-fade-leave-active {
    transition: all 0.3s ease;
}

.slide-side-fade-leave-from {
    opacity: 1;
    transform: translateX(0);
}

.slide-side-fade-leave-to {
    opacity: 0;
    transform: translateX(20px);
}

.slide-side-fade-enter-active {
    transition: all 0.3s ease;
}

.slide-side-fade-enter-from {
    opacity: 0;
    transform: translateX(20px);
}

.slide-side-fade-enter-to {
    opacity: 1;
    transform: translateX(0px);
}
</style>
